import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Loader2, AlertCircle, X } from 'lucide-react';
import api from '../lib/api';

interface Product {
  id?: string;
  product_id?: string;
  title: string;
  category: string;
  price?: string;
  price_eur?: number;
  price_aed?: number;
  markets: string[];
  status: string;
  compliance: string | Record<string, string>;
}

export default function Products() {
  const queryClient = useQueryClient();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [sourceUrl, setSourceUrl] = useState('');
  const [markets, setMarkets] = useState<string[]>(['DE']);
  const [autoPublish, setAutoPublish] = useState(true);

  const { data: products, isLoading, error } = useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const response = await api.get<Product[]>('/products');
      return response.data;
    },
  });

  const addProductMutation = useMutation({
    mutationFn: async (newProduct: { source_url: string; target_markets: string[]; auto_publish: boolean }) => {
      const response = await api.post('/products', newProduct);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      setIsAddModalOpen(false);
      setSourceUrl('');
      setMarkets(['DE']);
      setAutoPublish(true);
    },
  });

  const handleAddProduct = (e: React.FormEvent) => {
    e.preventDefault();
    if (!sourceUrl || markets.length === 0) return;
    addProductMutation.mutate({
      source_url: sourceUrl,
      target_markets: markets,
      auto_publish: autoPublish,
    });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <Loader2 className="h-8 w-8 text-primary-500 animate-spin" />
        <p className="text-gray-400">Loading products...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4 text-red-500">
        <AlertCircle className="h-8 w-8" />
        <p>Failed to load products. Please check if the API is running.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Products</h1>
          <p className="text-gray-400 mt-1">Manage your product catalog</p>
        </div>
        <button 
          onClick={() => setIsAddModalOpen(true)}
          className="btn-primary flex items-center"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Product
        </button>
      </div>

      <div className="card">
        {Array.isArray(products) && products.length > 0 ? (
          <table className="w-full">
            <thead>
              <tr className="text-left text-gray-400 text-sm border-b border-dark-700">
                <th className="pb-3 font-medium">Product</th>
                <th className="pb-3 font-medium">Category</th>
                <th className="pb-3 font-medium">Price</th>
                <th className="pb-3 font-medium">Markets</th>
                <th className="pb-3 font-medium">Status</th>
                <th className="pb-3 font-medium">Compliance</th>
                <th className="pb-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="text-gray-300">
              {products.map((product: Product) => (
                <tr key={product.id || product.product_id} className="border-b border-dark-800">
                  <td className="py-3">
                    <div>
                      <p className="font-medium text-white">{product.title}</p>
                      <p className="text-sm text-gray-500">{product.id || product.product_id}</p>
                    </div>
                  </td>
                  <td className="py-3">{product.category}</td>
                  <td className="py-3 font-medium">
                    {product.price || (product.price_eur ? `€${product.price_eur}` : `${product.price_aed} AED`)}
                  </td>
                  <td className="py-3">
                    <div className="flex space-x-1">
                      {Array.isArray(product.markets) && product.markets.map((m: string) => (
                        <span key={m} className="px-2 py-1 bg-dark-700 rounded text-xs">
                          {m}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-3">
                    <span className={`
                      px-2 py-1 rounded-full text-xs font-medium
                      ${product.status === 'active' || product.status === 'published' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}
                    `}>
                      {product.status}
                    </span>
                  </td>
                  <td className="py-3">
                    <div className="flex flex-col space-y-1">
                      {typeof product.compliance === 'object' && product.compliance !== null ? (
                        Object.entries(product.compliance).map(([market, status]: [string, any]) => (
                          <span key={market} className={`
                            px-2 py-1 rounded-full text-[10px] font-medium inline-block w-fit
                            ${status === 'approved' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'}
                          `}>
                            {market}: {status}
                          </span>
                        ))
                      ) : (
                        <span className={`
                          px-2 py-1 rounded-full text-xs font-medium
                          ${product.compliance === 'approved' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'}
                        `}>
                          {String(product.compliance || 'unknown')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-3">
                    <button className="text-primary-400 hover:text-primary-300 text-sm">
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="py-12 text-center">
            <p className="text-gray-400">No products found.</p>
          </div>
        )}
      </div>

      {/* Add Product Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-900 border border-dark-700 rounded-xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">Add New Product</h2>
              <button onClick={() => setIsAddModalOpen(false)} className="text-gray-400 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleAddProduct} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Source URL (AliExpress/1688)</label>
                <input
                  type="url"
                  required
                  value={sourceUrl}
                  onChange={(e) => setSourceUrl(e.target.value)}
                  className="w-full bg-dark-800 border border-dark-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-primary-500"
                  placeholder="https://..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Target Markets</label>
                <div className="flex space-x-6">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={markets.includes('DE')}
                      onChange={(e) => {
                        if (e.target.checked) setMarkets([...markets, 'DE']);
                        else setMarkets(markets.filter((m) => m !== 'DE'));
                      }}
                      className="rounded border-dark-700 bg-dark-800 text-primary-500 focus:ring-primary-500 h-4 w-4"
                    />
                    <span className="text-white text-sm">Germany (DE)</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={markets.includes('AE')}
                      onChange={(e) => {
                        if (e.target.checked) setMarkets([...markets, 'AE']);
                        else setMarkets(markets.filter((m) => m !== 'AE'));
                      }}
                      className="rounded border-dark-700 bg-dark-800 text-primary-500 focus:ring-primary-500 h-4 w-4"
                    />
                    <span className="text-white text-sm">UAE (AE)</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="flex items-center space-x-2 cursor-pointer mt-2">
                  <input
                    type="checkbox"
                    checked={autoPublish}
                    onChange={(e) => setAutoPublish(e.target.checked)}
                    className="rounded border-dark-700 bg-dark-800 text-primary-500 focus:ring-primary-500 h-4 w-4"
                  />
                  <span className="text-white text-sm">Auto-publish if compliant</span>
                </label>
              </div>

              {addProductMutation.isError && (
                <div className="text-red-500 text-sm mt-2">
                  Failed to initiate onboarding. Please try again.
                </div>
              )}

              <div className="flex justify-end space-x-3 mt-8">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="px-4 py-2 text-gray-400 hover:text-white font-medium text-sm transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addProductMutation.isPending || markets.length === 0}
                  className="btn-primary flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addProductMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Starting...
                    </>
                  ) : (
                    'Start Onboarding'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
